import React, { useEffect, useState, useCallback, useRef, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { getUserDisplayName } from '../utils/nameUtils';
import {
  EnvelopeIcon, EnvelopeOpenIcon, PencilSquareIcon, TrashIcon, BellAlertIcon,
  LockClosedIcon, GlobeAltIcon, ArrowLeftIcon, PlusIcon, XMarkIcon, CheckIcon,
  MicrophoneIcon, PauseIcon, ArrowUturnLeftIcon, SpeakerWaveIcon, UserGroupIcon
} from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import {
  createLetter, updateLetter, publishLetter, setLetterPublic, deleteLetter,
  fetchLetterContent, fetchLettersByAuthor, fetchLettersForMe, fetchAllFamilyLetters,
  sendLetterReminder, fetchMyUnreadReminders, markReminderRead,
  fetchLetterRecording, uploadLetterRecording, deleteLetterRecording,
  updateLetterRecipients
} from '../utils/letters';
import { getMicConstraints, chooseMimeType, getAudioDuration } from '../utils/micRecording';

const TABS = [
  { key: 'toFamily', label: 'Letters to Family' },
  { key: 'forMe', label: 'Letters for Me' },
  { key: 'all', label: 'All Family Letters', adminOnly: true },
];

function formatDate(ts) {
  if (!ts?.seconds) return '';
  return new Date(ts.seconds * 1000).toLocaleDateString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric'
  });
}

// Draft / recording chip for a letter's lifecycle. A small mic/speaker glyph
// rides along with "Recorded" so it's scannable at a glance, not just by
// reading the label.
function StatusChip({ letter }) {
  if (letter.status === 'draft') {
    return <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-gray-200 text-gray-600">Draft</span>;
  }
  if (letter.isRecorded) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700">
        <SpeakerWaveIcon className="w-3 h-3" /> Recorded
      </span>
    );
  }
  return <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-cerulean/10 text-cerulean">Awaiting recording</span>;
}

function PublicChip({ letter }) {
  if (!letter.isPublic) return null;
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-aqua-deep/10 text-aqua-deep">
      <GlobeAltIcon className="w-3 h-3" /> Public
    </span>
  );
}

function formatSeconds(total) {
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

// Natural letter recording: the author reads the whole letter like a
// teleprompter, pausing and continuing at will. This is ONE continuous
// MediaRecorder session (pause()/resume()), not separate per-section
// recordings — sections are extracted via requestData() at each pause and
// simply concatenated at the end. That avoids ever decoding recorded audio
// client-side (decodeAudioData cannot reliably decode the fragmented MP4
// iOS Safari produces, which made earlier takes sound corrupted).
function LetterRecorder({ familyId, letter, body, uid, onRecordedChange }) {
  const [existing, setExisting] = useState(undefined); // undefined = loading, null = none
  const [reRecording, setReRecording] = useState(false);
  const [sectionSeconds, setSectionSeconds] = useState([]); // committed section durations
  const [isRecording, setIsRecording] = useState(false);
  const [seconds, setSeconds] = useState(0); // in-progress section timer
  const [finalTake, setFinalTake] = useState(null); // { blob, url, duration }
  const [preparing, setPreparing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const mimeTypeRef = useRef('');
  const chunksRef = useRef([]); // one Blob per committed section, in order
  const timerRef = useRef(null);
  const currentSecondsRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    fetchLetterRecording(familyId, letter.id)
      .then(rec => { if (!cancelled) setExisting(rec); })
      .catch(e => {
        console.error('LetterRecorder: error loading recording', e);
        if (!cancelled) setExisting(null);
      });
    return () => { cancelled = true; };
  }, [familyId, letter.id]);

  const teardownSession = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    mediaRecorderRef.current = null;
    streamRef.current = null;
    chunksRef.current = [];
    clearInterval(timerRef.current);
  }, []);

  // Release mic/recorder when leaving the recorder entirely.
  useEffect(() => () => teardownSession(), [teardownSession]);

  const totalSeconds = sectionSeconds.reduce((sum, s) => sum + s, 0) + (isRecording ? seconds : 0);

  // Start the very first section, or resume after a pause.
  async function startOrResume() {
    setError('');
    setFinalTake(prev => { if (prev) URL.revokeObjectURL(prev.url); return null; });

    if (!mediaRecorderRef.current) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia(getMicConstraints());
        streamRef.current = stream;
        mimeTypeRef.current = chooseMimeType();
        console.log('LetterRecorder: starting session, mimeType =', mimeTypeRef.current || '(browser default)');
        const mediaRecorder = new MediaRecorder(stream, mimeTypeRef.current ? { mimeType: mimeTypeRef.current } : undefined);
        mediaRecorder.ondataavailable = e => {
          console.log(`LetterRecorder: ondataavailable — size=${e.data?.size ?? 0}, type=${e.data?.type}, chunksSoFar=${chunksRef.current.length}`);
          if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
        };
        mediaRecorder.onerror = e => console.error('LetterRecorder: MediaRecorder error', e.error || e);
        mediaRecorderRef.current = mediaRecorder;
        mediaRecorder.start();
      } catch (e) {
        console.error('LetterRecorder: microphone error', e);
        setError('Unable to access the microphone. Please check permissions.');
        return;
      }
    } else {
      mediaRecorderRef.current.resume();
    }

    setIsRecording(true);
    setSeconds(0);
    currentSecondsRef.current = 0;
    timerRef.current = setInterval(() => {
      currentSecondsRef.current += 1;
      setSeconds(s => s + 1);
    }, 1000);
  }

  // Pause: flush the in-progress audio as its own section chunk, keep the
  // recorder (and mic) alive so Continue is instant.
  function pauseSection() {
    const recorder = mediaRecorderRef.current;
    if (!recorder || !isRecording) return;
    console.log(`LetterRecorder: pause — recorder.state=${recorder.state}, section was ${currentSecondsRef.current}s, chunksBefore=${chunksRef.current.length}`);
    clearInterval(timerRef.current);
    setIsRecording(false);
    recorder.requestData();
    recorder.pause();
    console.log(`LetterRecorder: after requestData()+pause() — recorder.state=${recorder.state}, chunksAfter=${chunksRef.current.length}`);
    setSectionSeconds(prev => [...prev, currentSecondsRef.current]);
  }

  // Redo: drop the last committed section chunk. The recorder stays paused;
  // the next Continue resumes cleanly and re-reads that stretch fresh.
  function redoLastSection() {
    if (sectionSeconds.length === 0) return;
    chunksRef.current = chunksRef.current.slice(0, -1);
    setSectionSeconds(prev => prev.slice(0, -1));
    setFinalTake(prev => { if (prev) URL.revokeObjectURL(prev.url); return null; });
  }

  function discardAll() {
    if (!window.confirm('Discard this recording session and start over?')) return;
    teardownSession();
    setSectionSeconds([]);
    setSeconds(0);
    setIsRecording(false);
    setFinalTake(prev => { if (prev) URL.revokeObjectURL(prev.url); return null; });
  }

  // Finish: concatenate the committed section chunks (no decoding involved)
  // and preview the result before saving.
  async function finishReading() {
    if (chunksRef.current.length === 0) return;
    setPreparing(true);
    setError('');
    try {
      const sizes = chunksRef.current.map(c => c.size);
      const blob = new Blob(chunksRef.current, { type: mimeTypeRef.current || 'audio/webm' });
      console.log(`LetterRecorder: finishing — ${chunksRef.current.length} chunk(s), sizes=[${sizes.join(', ')}], totalBytes=${blob.size}, type=${blob.type}`);
      const duration = await getAudioDuration(blob);
      console.log(`LetterRecorder: resolved duration = ${duration}s`);
      setFinalTake({ blob, url: URL.createObjectURL(blob), duration });
    } catch (e) {
      console.error('LetterRecorder: error preparing recording', e);
      setError(`Could not prepare the recording: ${e.message || 'unknown error'}`);
    } finally {
      setPreparing(false);
    }
  }

  async function saveRecording() {
    if (!finalTake) return;
    setUploading(true);
    setError('');
    try {
      const { audioUrl, storagePath } = await uploadLetterRecording(familyId, letter.id, {
        authorId: uid,
        blob: finalTake.blob,
        duration: finalTake.duration,
      });
      teardownSession();
      URL.revokeObjectURL(finalTake.url);
      setSectionSeconds([]);
      setFinalTake(null);
      setReRecording(false);
      setExisting({ id: 'main', audioUrl, duration: Math.round(finalTake.duration), storagePath });
      onRecordedChange(true);
    } catch (e) {
      console.error('LetterRecorder: error saving recording', e);
      setError(`Could not save the recording: ${e.message || 'unknown error'}`);
    } finally {
      setUploading(false);
    }
  }

  async function removeRecording() {
    if (!existing) return;
    if (!window.confirm('Delete the recording of this letter? The written letter is kept.')) return;
    try {
      await deleteLetterRecording(familyId, letter.id, existing);
      setExisting(null);
      onRecordedChange(false);
    } catch (e) {
      console.error('LetterRecorder: error deleting recording', e);
      setError('Could not delete the recording.');
    }
  }

  if (existing === undefined) {
    return <p className="text-sm text-gray-400 text-center py-8">Loading recorder&hellip;</p>;
  }

  // A finished recording exists and we're not re-recording: play/manage it.
  if (existing && !reRecording) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 rounded-xl bg-green-50 border border-green-200 px-3 py-2.5">
          <CheckCircleIcon className="w-5 h-5 text-green-500 flex-shrink-0" />
          <p className="text-xs text-green-800">
            This letter is recorded ({formatSeconds(existing.duration || 0)}).
          </p>
        </div>
        <audio src={existing.audioUrl} controls className="w-full" />
        <button type="button" onClick={() => setReRecording(true)} className="btn-secondary w-full flex items-center justify-center gap-2">
          <MicrophoneIcon className="w-4 h-4" /> Record Again
        </button>
        <button
          type="button"
          onClick={removeRecording}
          className="w-full text-xs text-red-500 hover:text-red-700 py-1"
        >
          Delete this recording
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <div className="px-3 py-2 rounded-lg bg-red-50 text-red-700 text-sm">{error}</div>
      )}

      {/* Teleprompter: the whole letter, read at will */}
      <div className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-4 max-h-72 overflow-y-auto">
        <p className="text-base text-gray-900 whitespace-pre-wrap leading-relaxed">{body}</p>
      </div>

      {/* Session progress */}
      {(sectionSeconds.length > 0 || isRecording) && !finalTake && (
        <p className="text-xs text-gray-500 text-center">
          {formatSeconds(totalSeconds)} recorded
          {sectionSeconds.length > 0 && <> &middot; {sectionSeconds.length} section{sectionSeconds.length === 1 ? '' : 's'}</>}
          {isRecording && <span className="text-red-500 font-medium animate-pulse"> &middot; recording&hellip;</span>}
        </p>
      )}

      {finalTake ? (
        /* Preview the assembled letter before saving */
        <div className="space-y-3">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Preview your letter ({formatSeconds(Math.round(finalTake.duration))})</p>
          <audio src={finalTake.url} controls className="w-full" />
          <div className="flex gap-2">
            <button
              type="button"
              disabled={uploading}
              onClick={() => setFinalTake(prev => { if (prev) URL.revokeObjectURL(prev.url); return null; })}
              className="btn-secondary flex-1"
            >
              Keep Editing
            </button>
            <button type="button" disabled={uploading} onClick={saveRecording} className="btn-primary flex-1 flex items-center justify-center gap-2">
              <CheckIcon className="w-4 h-4" /> {uploading ? 'Saving…' : 'Save Recording'}
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-3">
          {isRecording ? (
            <>
              <button
                type="button"
                onClick={pauseSection}
                className="w-16 h-16 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center shadow-md transition-colors"
              >
                <PauseIcon className="w-7 h-7" />
              </button>
              <p className="text-sm font-medium text-red-500 animate-pulse">Reading&hellip; tap to pause</p>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={startOrResume}
                className="w-16 h-16 rounded-full bg-gradient-to-r from-cerulean to-aqua-deep hover:shadow-lg text-white flex items-center justify-center shadow-md transition-all"
              >
                <MicrophoneIcon className="w-7 h-7" />
              </button>
              <p className="text-sm text-gray-500">
                {sectionSeconds.length === 0 ? 'Tap to start reading your letter' : 'Tap to continue reading'}
              </p>
            </>
          )}

          {sectionSeconds.length > 0 && !isRecording && (
            <div className="w-full space-y-2 pt-1">
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={redoLastSection}
                  className="btn-secondary flex-1 flex items-center justify-center gap-2"
                >
                  <ArrowUturnLeftIcon className="w-4 h-4" /> Redo Last Section
                </button>
                <button
                  type="button"
                  disabled={preparing}
                  onClick={finishReading}
                  className="btn-primary flex-1 flex items-center justify-center gap-2"
                >
                  <CheckIcon className="w-4 h-4" /> {preparing ? 'Preparing…' : 'Finish'}
                </button>
              </div>
              <button
                type="button"
                onClick={discardAll}
                className="w-full text-xs text-red-500 hover:text-red-700 py-1"
              >
                Discard and start over
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function LetterBox({ open, onClose, initialTab = 'toFamily', currentUser }) {
  const uid = currentUser?.uid;

  const [families, setFamilies] = useState([]);
  const [selectedFamilyId, setSelectedFamilyId] = useState('');
  const [membersById, setMembersById] = useState({});
  const [activeTab, setActiveTab] = useState(initialTab);
  const [letters, setLetters] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('list'); // 'list' | 'compose' | 'detail'
  const [error, setError] = useState('');

  const PAGE_SIZE = 10;
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [lastDoc, setLastDoc] = useState(null);
  // Client-side pagination cache for toFamily and forMe tabs (full fetched set,
  // sliced on display). Only the admin `all` tab uses Firestore cursor pagination.
  const allLettersRef = useRef([]);
  const [lettersPage, setLettersPage] = useState(1);
  // Prevents Effect 2 (tab/family switch) from firing while Effect 1 (init) is
  // still running its async work. Without this guard, setSelectedFamilyId()
  // inside Effect 1 triggers Effect 2, causing a race condition.
  const initDoneRef = useRef(false);

  // Compose state
  const [editingLetter, setEditingLetter] = useState(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [toEveryone, setToEveryone] = useState(true);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [saving, setSaving] = useState(false);

  // Detail state
  const [detailLetter, setDetailLetter] = useState(null);
  const [detailContent, setDetailContent] = useState(null);
  const [detailLocked, setDetailLocked] = useState(false);
  const [detailRecording, setDetailRecording] = useState(null);
  const [remindedIds, setRemindedIds] = useState([]);

  // Manage-recipients state (add/remove who this letter goes to, without
  // touching subject/body/recording)
  const [manageToEveryone, setManageToEveryone] = useState(true);
  const [manageRecipients, setManageRecipients] = useState([]);
  const [savingRecipients, setSavingRecipients] = useState(false);

  const selectedFamily = families.find(f => f.id === selectedFamilyId);
  const isAdmin = !!selectedFamily &&
    (selectedFamily.createdBy === uid || (selectedFamily.adminIDs || []).includes(uid));
  // Show all tabs while loading (admin status unknown); filter once data is in.
  // Prevents the 2→3 tab flash for admin users on first open.
  const visibleTabs = loading ? TABS : TABS.filter(t => !t.adminOnly || isAdmin);

  const memberName = useCallback(
    (id) => (id === uid ? 'You' : getUserDisplayName(membersById[id])),
    [membersById, uid]
  );

  // Per-tab first-page fetcher.
  // toFamily + forMe: fetch all (up to their cap), return { letters, hasMore, all }
  //   for client-side slicing — no composite index required.
  // all (admin): Firestore cursor pagination, returns { letters, lastDoc, hasMore }.
  async function fetchPageForTab(famId, tab, userId, isAdm, afterDoc) {
    if (tab === 'toFamily') {
      const all = await fetchLettersByAuthor(famId, userId);
      return { letters: all.slice(0, PAGE_SIZE), hasMore: all.length > PAGE_SIZE, all };
    }
    if (tab === 'forMe') {
      const all = await fetchLettersForMe(famId, userId);
      return { letters: all.slice(0, PAGE_SIZE), hasMore: all.length > PAGE_SIZE, all };
    }
    if (tab === 'all' && isAdm) {
      return fetchAllFamilyLetters(famId, { pageSize: PAGE_SIZE, afterDoc });
    }
    return { letters: [], hasMore: false };
  }

  // Combined init: load families, member profiles, and first page of letters.
  // Sets loading=true immediately so the empty state never flashes.
  useEffect(() => {
    if (!open || !uid) return;
    initDoneRef.current = false;
    setActiveTab(initialTab);
    setView('list');
    setError('');
    setLetters([]);
    setHasMore(false);
    setLastDoc(null);
    allLettersRef.current = [];
    setLettersPage(1);
    setLoading(true);

    let cancelled = false;
    (async () => {
      try {
        const userDoc = await getDoc(doc(db, 'users', uid));
        const familyIds = userDoc.data()?.familyIds || [];
        const loaded = [];
        const profiles = {};
        for (const fid of familyIds) {
          const famDoc = await getDoc(doc(db, 'families', fid));
          if (!famDoc.exists()) continue;
          const fam = { id: famDoc.id, ...famDoc.data() };
          loaded.push(fam);
          for (const memberId of fam.memberIDs || []) {
            if (profiles[memberId]) continue;
            const p = await getDoc(doc(db, 'users', memberId));
            if (p.exists()) profiles[memberId] = p.data();
          }
        }
        if (cancelled) return;
        setFamilies(loaded);
        setMembersById(profiles);
        const famId = loaded[0]?.id ?? '';
        setSelectedFamilyId(famId);

        if (!famId) return;
        const firstFam = loaded[0];
        const isAdm = !!(firstFam && (firstFam.createdBy === uid || (firstFam.adminIDs || []).includes(uid)));
        const [lettersData, rems] = await Promise.all([
          fetchPageForTab(famId, initialTab, uid, isAdm, null),
          fetchMyUnreadReminders(famId, uid),
        ]);
        if (cancelled) return;
        setLetters(lettersData.letters);
        setLastDoc(lettersData.lastDoc ?? null);
        setHasMore(lettersData.hasMore ?? false);
        if (lettersData.all) { allLettersRef.current = lettersData.all; }
        setReminders(rems);
      } catch (e) {
        if (!cancelled) { console.error('LetterBox init:', e); setError('Could not load your family information.'); }
      } finally {
        if (!cancelled) {
          initDoneRef.current = true;
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [open, uid, initialTab]);

  // Tab/family switch after init — reloads the first page for the new selection.
  useEffect(() => {
    if (!open || !selectedFamilyId || !uid || view !== 'list') return;
    if (!initDoneRef.current) return; // skip: Effect 1 (init) hasn't finished yet
    setLetters([]);
    setHasMore(false);
    setLastDoc(null);
    allLettersRef.current = [];
    setLettersPage(1);
    setLoading(true);
    setError('');

    let cancelled = false;
    (async () => {
      try {
        const [lettersData, rems] = await Promise.all([
          fetchPageForTab(selectedFamilyId, activeTab, uid, isAdmin, null),
          fetchMyUnreadReminders(selectedFamilyId, uid),
        ]);
        if (cancelled) return;
        setLetters(lettersData.letters);
        setLastDoc(lettersData.lastDoc ?? null);
        setHasMore(lettersData.hasMore ?? false);
        if (lettersData.all) { allLettersRef.current = lettersData.all; }
        setReminders(rems);
      } catch (e) {
        if (!cancelled) setError('Could not load letters.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedFamilyId, open, view]);

  // If the admin-only tab is active but the user isn't admin of this family, bounce back.
  useEffect(() => {
    if (activeTab === 'all' && selectedFamily && !isAdmin) setActiveTab('toFamily');
  }, [activeTab, selectedFamily, isAdmin]);

  const refreshLetters = useCallback(async () => {
    if (!selectedFamilyId || !uid) return;
    setLoading(true);
    setError('');
    try {
      const data = await fetchPageForTab(selectedFamilyId, activeTab, uid, isAdmin, null);
      setLetters(data.letters);
      setLastDoc(data.lastDoc ?? null);
      setHasMore(data.hasMore ?? false);
      if (data.all) { allLettersRef.current = data.all; }
      setLettersPage(1);
      setReminders(await fetchMyUnreadReminders(selectedFamilyId, uid));
    } catch (e) {
      console.error('LetterBox: error refreshing letters', e);
      setError('Could not load letters.');
    } finally {
      setLoading(false);
    }
  }, [selectedFamilyId, uid, activeTab, isAdmin]);

  async function loadMore() {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      if (activeTab === 'all') {
        // Only the admin tab uses Firestore cursor pagination.
        const data = await fetchAllFamilyLetters(selectedFamilyId, { pageSize: PAGE_SIZE, afterDoc: lastDoc });
        setLetters(prev => [...prev, ...data.letters]);
        setLastDoc(data.lastDoc ?? null);
        setHasMore(data.hasMore ?? false);
      } else {
        // toFamily and forMe: slice from the cached full array.
        const next = lettersPage + 1;
        setLettersPage(next);
        setLetters(allLettersRef.current.slice(0, next * PAGE_SIZE));
        setHasMore(allLettersRef.current.length > next * PAGE_SIZE);
      }
    } catch (e) {
      setError('Could not load more letters.');
    } finally {
      setLoadingMore(false);
    }
  }

  function openCompose(letter = null) {
    setEditingLetter(letter);
    setSubject(letter?.subject || '');
    setToEveryone(letter ? !!letter.toEveryone : true);
    setSelectedRecipients(letter?.recipientIds || []);
    setBody('');
    if (letter) {
      fetchLetterContent(selectedFamilyId, letter.id)
        .then(content => setBody(content?.body || ''))
        .catch(e => console.error('LetterBox: error loading letter body', e));
    }
    setView('compose');
  }

  function openManageRecipients(letter) {
    setManageToEveryone(!!letter.toEveryone);
    setManageRecipients(letter.recipientIds || []);
    setError('');
    setView('manage');
  }

  async function handleSaveRecipients() {
    if (!manageToEveryone && manageRecipients.length === 0) {
      setError('Choose at least one recipient, or address it to everyone.');
      return;
    }
    setSavingRecipients(true);
    setError('');
    try {
      await updateLetterRecipients(selectedFamilyId, detailLetter.id, {
        recipientIds: manageRecipients,
        toEveryone: manageToEveryone,
      });
      setDetailLetter(prev => (prev ? { ...prev, recipientIds: manageRecipients, toEveryone: manageToEveryone } : prev));
      setView('detail');
    } catch (e) {
      console.error('LetterBox: error updating recipients', e);
      setError('Could not update recipients. Please try again.');
    } finally {
      setSavingRecipients(false);
    }
  }

  async function openDetail(letter) {
    setDetailLetter(letter);
    setDetailContent(null);
    setDetailLocked(false);
    setDetailRecording(null);
    setRemindedIds([]);
    setView('detail');
    try {
      const content = await fetchLetterContent(selectedFamilyId, letter.id);
      setDetailContent(content);
      if (letter.isRecorded) {
        setDetailRecording(await fetchLetterRecording(selectedFamilyId, letter.id));
      }
    } catch (e) {
      // Expected for the admin view of a private letter — existence only.
      setDetailLocked(true);
    }
  }

  async function handleSave(publish) {
    if (!subject.trim()) { setError('Please add a subject.'); return; }
    if (!body.trim()) { setError('Please write or paste the letter body.'); return; }
    if (!toEveryone && selectedRecipients.length === 0) {
      setError('Choose at least one recipient, or address it to everyone.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (editingLetter) {
        await updateLetter(selectedFamilyId, editingLetter.id, {
          subject: subject.trim(), body, recipientIds: selectedRecipients, toEveryone
        });
        if (publish && editingLetter.status === 'draft') {
          await publishLetter(selectedFamilyId, editingLetter.id);
        }
      } else {
        await createLetter(selectedFamilyId, {
          authorId: uid,
          authorName: getUserDisplayName(membersById[uid]) || currentUser.displayName || currentUser.email,
          subject: subject.trim(), body, recipientIds: selectedRecipients, toEveryone, publish
        });
      }
      setView('list');
      setEditingLetter(null);
    } catch (e) {
      console.error('LetterBox: error saving letter', e);
      setError('Could not save the letter. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  async function handlePublish(letter) {
    try {
      await publishLetter(selectedFamilyId, letter.id);
      setDetailLetter({ ...letter, status: 'published' });
      refreshLetters();
    } catch (e) {
      console.error('LetterBox: error publishing letter', e);
      setError('Could not publish the letter.');
    }
  }

  async function handleTogglePublic(letter) {
    try {
      await setLetterPublic(selectedFamilyId, letter.id, !letter.isPublic);
      setDetailLetter({ ...letter, isPublic: !letter.isPublic });
    } catch (e) {
      console.error('LetterBox: error updating letter visibility', e);
      setError('Could not change the letter’s visibility.');
    }
  }

  async function handleDelete(letter) {
    if (!window.confirm('Delete this letter? This cannot be undone.')) return;
    try {
      await deleteLetter(selectedFamilyId, letter.id);
      setView('list');
    } catch (e) {
      console.error('LetterBox: error deleting letter', e);
      setError('Could not delete the letter.');
    }
  }

  async function handleRemind(letter, toUserId, kind) {
    try {
      await sendLetterReminder(selectedFamilyId, {
        letterId: letter.id,
        letterSubject: letter.subject,
        fromUserId: uid,
        fromName: getUserDisplayName(membersById[uid]),
        toUserId,
        kind,
      });
      setRemindedIds(prev => [...prev, `${kind}:${toUserId}`]);
    } catch (e) {
      console.error('LetterBox: error sending reminder', e);
      setError('Could not send the reminder.');
    }
  }

  async function dismissReminder(reminder) {
    try {
      await markReminderRead(selectedFamilyId, reminder.id);
      setReminders(prev => prev.filter(r => r.id !== reminder.id));
    } catch (e) {
      console.error('LetterBox: error dismissing reminder', e);
    }
  }

  function recipientSummary(letter) {
    if (letter.toEveryone) return 'Everyone in the family';
    const names = (letter.recipientIds || []).map(memberName);
    return names.join(', ') || 'No recipients';
  }

  // Members the admin can nudge to read a published letter.
  function reminderTargets(letter) {
    if (letter.toEveryone) {
      return (selectedFamily?.memberIDs || []).filter(id => id !== letter.authorId);
    }
    return letter.recipientIds || [];
  }

  const isRecipientOfDetail = detailLetter && (
    detailLetter.toEveryone || (detailLetter.recipientIds || []).includes(uid)
  ) && detailLetter.authorId !== uid;
  const isAuthorOfDetail = detailLetter?.authorId === uid;

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-[10000]" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-in-out duration-300" enterFrom="opacity-0" enterTo="opacity-100"
          leave="ease-in-out duration-300" leaveFrom="opacity-100" leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-300" enterFrom="translate-x-full" enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-300" leaveFrom="translate-x-0" leaveTo="translate-x-full"
              >
                <Dialog.Panel className="pointer-events-auto w-screen max-w-md">
                  <div className="flex h-full flex-col bg-white shadow-xl">
                    {/* Header */}
                    <div className="flex items-center justify-between px-4 py-4 border-b border-gray-200">
                      <div className="flex items-center gap-2">
                        {view !== 'list' ? (
                          <button
                            type="button"
                            onClick={() => { setView(view === 'record' || view === 'manage' ? 'detail' : 'list'); setError(''); }}
                            className="p-1 rounded-lg text-gray-500 hover:bg-gray-100"
                          >
                            <ArrowLeftIcon className="w-5 h-5" />
                          </button>
                        ) : (
                          <EnvelopeIcon className="w-5 h-5 text-cerulean" />
                        )}
                        <Dialog.Title className="text-lg font-medium text-gray-900">
                          {view === 'compose' ? (editingLetter ? 'Edit Letter' : 'New Letter')
                            : view === 'record' ? 'Record Letter'
                            : view === 'manage' ? 'Manage Recipients'
                            : 'Letter Box'}
                        </Dialog.Title>
                      </div>
                      <button
                        type="button"
                        onClick={onClose}
                        className="p-1 rounded-lg text-gray-400 hover:bg-gray-100"
                      >
                        <XMarkIcon className="w-5 h-5" />
                      </button>
                    </div>

                    {/* Family picker (only when the user belongs to more than one family) */}
                    {view === 'list' && families.length > 1 && (
                      <div className="px-4 pt-3">
                        <select
                          value={selectedFamilyId}
                          onChange={e => setSelectedFamilyId(e.target.value)}
                          className="input-field text-sm"
                        >
                          {families.map(f => (
                            <option key={f.id} value={f.id}>{f.name || 'Family'}</option>
                          ))}
                        </select>
                      </div>
                    )}

                    {/* Tabs */}
                    {view === 'list' && (
                      <div className="px-4 pt-3">
                        <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
                          {visibleTabs.map(tab => (
                            <button
                              key={tab.key}
                              type="button"
                              onClick={() => setActiveTab(tab.key)}
                              className={`flex-1 text-xs font-semibold px-2 py-2 rounded-lg transition-colors ${
                                activeTab === tab.key
                                  ? 'bg-white text-cerulean shadow-sm'
                                  : 'text-gray-500 hover:text-gray-700'
                              }`}
                            >
                              {tab.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {error && (
                      <div className="mx-4 mt-3 px-3 py-2 rounded-lg bg-red-50 text-red-700 text-sm">
                        {error}
                      </div>
                    )}

                    {/* Body */}
                    <div className="flex-1 overflow-y-auto px-4 py-4">
                      {view === 'list' && (
                        <>
                          {/* Unread reminders for me */}
                          {reminders.length > 0 && (
                            <div className="mb-4 space-y-2">
                              {reminders.map(reminder => (
                                <div key={reminder.id} className="flex items-start gap-2 rounded-xl bg-amber-50 border border-amber-200 px-3 py-2.5">
                                  <BellAlertIcon className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                                  <p className="text-xs text-amber-800 flex-1">
                                    {reminder.kind === 'finish_draft'
                                      ? <><span className="font-semibold">{reminder.fromName}</span> encourages you to finish your draft letter{reminder.letterSubject ? <> &ldquo;{reminder.letterSubject}&rdquo;</> : ''}.</>
                                      : <><span className="font-semibold">{reminder.fromName}</span> reminds you: a letter is waiting for you{reminder.letterSubject ? <> &mdash; &ldquo;{reminder.letterSubject}&rdquo;</> : ''}.</>}
                                  </p>
                                  <button
                                    type="button"
                                    onClick={() => dismissReminder(reminder)}
                                    className="text-amber-400 hover:text-amber-600"
                                  >
                                    <XMarkIcon className="w-4 h-4" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}

                          {activeTab === 'toFamily' && (
                            <button type="button" onClick={() => openCompose()} className="btn-primary w-full flex items-center justify-center gap-2 mb-4">
                              <PlusIcon className="w-5 h-5" /> Create a Letter
                            </button>
                          )}

                          {loading ? (
                            <p className="text-sm text-gray-400 text-center py-8">Loading letters&hellip;</p>
                          ) : letters.length === 0 ? (
                            <div className="text-center py-10">
                              <EnvelopeOpenIcon className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                              <p className="text-sm text-gray-500">
                                {activeTab === 'toFamily' && 'You haven’t written any letters yet. Write words of love, memory, and encouragement your family can keep forever.'}
                                {activeTab === 'forMe' && 'No letters for you yet.'}
                                {activeTab === 'all' && 'No letters in this family yet.'}
                              </p>
                            </div>
                          ) : (
                            <div className="space-y-2">
                              {letters.map(letter => (
                                <button
                                  key={letter.id}
                                  type="button"
                                  onClick={() => openDetail(letter)}
                                  className="w-full text-left rounded-2xl border border-gray-200 hover:border-cerulean/40 hover:bg-gray-50 transition-colors px-4 py-3"
                                >
                                  <div className="flex items-center justify-between gap-2 mb-1">
                                    <span className="font-semibold text-sm text-gray-900 truncate">{letter.subject}</span>
                                    <span className="text-[11px] text-gray-400 flex-shrink-0">{formatDate(letter.createdAt)}</span>
                                  </div>
                                  <p className="text-xs text-gray-500 truncate mb-1.5">
                                    {activeTab === 'toFamily'
                                      ? <>To: {recipientSummary(letter)}</>
                                      : <>From: {memberName(letter.authorId) || letter.authorName}{activeTab === 'all' && <> &middot; To: {recipientSummary(letter)}</>}</>}
                                  </p>
                                  <div className="flex items-center gap-1.5">
                                    <StatusChip letter={letter} />
                                    <PublicChip letter={letter} />
                                  </div>
                                </button>
                              ))}
                              {hasMore && (
                                <button
                                  type="button"
                                  onClick={loadMore}
                                  disabled={loadingMore}
                                  className="w-full mt-3 py-2 text-sm font-medium text-cerulean hover:text-cerulean/80 disabled:opacity-50"
                                >
                                  {loadingMore ? 'Loading…' : 'Load more'}
                                </button>
                              )}
                            </div>
                          )}
                        </>
                      )}

                      {view === 'compose' && (
                        <div className="space-y-4">
                          <div>
                            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">To</label>
                            <div className="space-y-2">
                              <label className="flex items-center gap-2 text-sm text-gray-800">
                                <input
                                  type="radio"
                                  checked={toEveryone}
                                  onChange={() => setToEveryone(true)}
                                  className="text-cerulean focus:ring-cerulean"
                                />
                                Everyone in the family <span className="text-xs text-gray-400">(includes future members)</span>
                              </label>
                              <label className="flex items-center gap-2 text-sm text-gray-800">
                                <input
                                  type="radio"
                                  checked={!toEveryone}
                                  onChange={() => setToEveryone(false)}
                                  className="text-cerulean focus:ring-cerulean"
                                />
                                Specific family members
                              </label>
                              {!toEveryone && (
                                <div className="ml-6 space-y-1.5 max-h-40 overflow-y-auto rounded-xl border border-gray-200 p-3">
                                  {(selectedFamily?.memberIDs || []).filter(id => id !== uid).map(memberId => (
                                    <label key={memberId} className="flex items-center gap-2 text-sm text-gray-800">
                                      <input
                                        type="checkbox"
                                        checked={selectedRecipients.includes(memberId)}
                                        onChange={e => setSelectedRecipients(prev =>
                                          e.target.checked ? [...prev, memberId] : prev.filter(id => id !== memberId)
                                        )}
                                        className="rounded text-cerulean focus:ring-cerulean"
                                      />
                                      {memberName(memberId)}
                                    </label>
                                  ))}
                                  {(selectedFamily?.memberIDs || []).filter(id => id !== uid).length === 0 && (
                                    <p className="text-xs text-gray-400">No other family members yet.</p>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>

                          <div>
                            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Subject</label>
                            <input
                              type="text"
                              value={subject}
                              onChange={e => setSubject(e.target.value)}
                              placeholder="e.g. To my beloved daughter"
                              className="input-field"
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Letter</label>
                            <textarea
                              value={body}
                              onChange={e => setBody(e.target.value)}
                              rows={10}
                              placeholder="Type or paste your letter here. Once saved, you can record yourself reading it aloud — one natural reading, with pause and continue."
                              className="input-field resize-y"
                            />
                          </div>

                          {editingLetter?.isRecorded && (
                            <p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
                              This letter is already recorded. If you change the words, the recording will no longer match — consider re-recording after saving.
                            </p>
                          )}

                          <div className="flex gap-2 pt-2">
                            <button
                              type="button"
                              disabled={saving}
                              onClick={() => handleSave(false)}
                              className="btn-secondary flex-1"
                            >
                              {editingLetter && editingLetter.status !== 'draft' ? 'Save Changes' : 'Save Draft'}
                            </button>
                            {(!editingLetter || editingLetter.status === 'draft') && (
                              <button
                                type="button"
                                disabled={saving}
                                onClick={() => handleSave(true)}
                                className="btn-primary flex-1"
                              >
                                {saving ? 'Saving…' : 'Save & Publish'}
                              </button>
                            )}
                          </div>
                        </div>
                      )}

                      {view === 'detail' && detailLetter && (
                        <div className="space-y-4">
                          <div>
                            <div className="flex items-start justify-between gap-2">
                              <h3 className="text-base font-semibold text-gray-900">{detailLetter.subject}</h3>
                              <span className="text-[11px] text-gray-400 flex-shrink-0 mt-1">{formatDate(detailLetter.createdAt)}</span>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              From: {memberName(detailLetter.authorId) || detailLetter.authorName}
                            </p>
                            <p className="text-xs text-gray-500">To: {recipientSummary(detailLetter)}</p>
                            <div className="flex items-center gap-1.5 mt-2">
                              <StatusChip letter={detailLetter} />
                              <PublicChip letter={detailLetter} />
                            </div>
                          </div>

                          {detailLocked ? (
                            <div className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-6 text-center">
                              <LockClosedIcon className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                              <p className="text-sm text-gray-500">
                                This letter is private. Only its recipients can read or listen to it &mdash;
                                but you can send them a gentle reminder that it&rsquo;s waiting.
                              </p>
                            </div>
                          ) : detailContent ? (
                            <>
                              {detailRecording && (
                                <div className="rounded-2xl border border-cerulean/20 bg-cerulean/5 px-4 py-3 space-y-2">
                                  <p className="text-xs font-semibold text-cerulean uppercase tracking-wider">
                                    Listen &mdash; read by {memberName(detailLetter.authorId) || detailLetter.authorName}
                                  </p>
                                  <audio src={detailRecording.audioUrl} controls className="w-full" />
                                </div>
                              )}
                              <div className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-4">
                                <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">{detailContent.body}</p>
                              </div>
                            </>
                          ) : (
                            <p className="text-sm text-gray-400 text-center py-6">Loading&hellip;</p>
                          )}

                          {/* Author actions */}
                          {isAuthorOfDetail && (
                            <div className="space-y-2">
                              {detailLetter.status === 'draft' && (
                                <button type="button" onClick={() => handlePublish(detailLetter)} className="btn-primary w-full flex items-center justify-center gap-2">
                                  <CheckIcon className="w-4 h-4" /> Publish Letter
                                </button>
                              )}
                              {!!detailContent?.body && (
                                <button
                                  type="button"
                                  onClick={() => setView('record')}
                                  className="btn-primary w-full flex items-center justify-center gap-2"
                                >
                                  <MicrophoneIcon className="w-4 h-4" />
                                  {detailLetter.isRecorded ? 'Listen or Re-record' : 'Record This Letter'}
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={() => openManageRecipients(detailLetter)}
                                className="btn-secondary w-full flex items-center justify-center gap-2"
                              >
                                <UserGroupIcon className="w-4 h-4" /> Manage Recipients
                              </button>
                              <div className="flex gap-2">
                                <button
                                  type="button"
                                  onClick={() => openCompose(detailLetter)}
                                  className="btn-secondary flex-1 flex items-center justify-center gap-2"
                                >
                                  <PencilSquareIcon className="w-4 h-4" /> Edit
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDelete(detailLetter)}
                                  className="flex-1 flex items-center justify-center gap-2 bg-white hover:bg-red-50 text-red-600 font-medium py-2 px-4 rounded-lg border border-red-200 transition-colors"
                                >
                                  <TrashIcon className="w-4 h-4" /> Delete
                                </button>
                              </div>
                            </div>
                          )}

                          {/* Public/private toggle: author or recipient, published letters only */}
                          {detailLetter.status === 'published' && (isAuthorOfDetail || isRecipientOfDetail) && (
                            <button
                              type="button"
                              onClick={() => handleTogglePublic(detailLetter)}
                              className="btn-secondary w-full flex items-center justify-center gap-2"
                            >
                              {detailLetter.isPublic
                                ? <><LockClosedIcon className="w-4 h-4" /> Make Private Again</>
                                : <><GlobeAltIcon className="w-4 h-4" /> Make Public for the Whole Family</>}
                            </button>
                          )}

                          {/* Admin reminders */}
                          {isAdmin && !isAuthorOfDetail && detailLetter.status === 'draft' && (
                            <button
                              type="button"
                              disabled={remindedIds.includes(`finish_draft:${detailLetter.authorId}`)}
                              onClick={() => handleRemind(detailLetter, detailLetter.authorId, 'finish_draft')}
                              className="btn-secondary w-full flex items-center justify-center gap-2 disabled:opacity-60"
                            >
                              <BellAlertIcon className="w-4 h-4" />
                              {remindedIds.includes(`finish_draft:${detailLetter.authorId}`)
                                ? 'Encouragement sent'
                                : `Encourage ${memberName(detailLetter.authorId)} to finish this letter`}
                            </button>
                          )}
                          {isAdmin && detailLetter.status === 'published' && (
                            <div className="rounded-2xl border border-gray-200 p-3 space-y-2">
                              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Remind a recipient</p>
                              {reminderTargets(detailLetter).filter(id => id !== uid).map(memberId => (
                                <div key={memberId} className="flex items-center justify-between gap-2">
                                  <span className="text-sm text-gray-800">{memberName(memberId)}</span>
                                  <button
                                    type="button"
                                    disabled={remindedIds.includes(`read_letter:${memberId}`)}
                                    onClick={() => handleRemind(detailLetter, memberId, 'read_letter')}
                                    className="text-xs font-semibold text-cerulean bg-cerulean/10 hover:bg-cerulean/20 rounded-full px-3 py-1 transition-colors disabled:opacity-60"
                                  >
                                    {remindedIds.includes(`read_letter:${memberId}`) ? 'Reminded ✓' : 'Remind'}
                                  </button>
                                </div>
                              ))}
                              {reminderTargets(detailLetter).filter(id => id !== uid).length === 0 && (
                                <p className="text-xs text-gray-400">No recipients to remind.</p>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      {view === 'manage' && detailLetter && (
                        <div className="space-y-4">
                          <p className="text-sm text-gray-500">
                            Add or remove who can read &ldquo;{detailLetter.subject}&rdquo;. Changes apply immediately — a
                            removed recipient loses access right away, and a newly added one sees it the next time they open Letter Box.
                          </p>
                          <div className="space-y-2">
                            <label className="flex items-center gap-2 text-sm text-gray-800">
                              <input
                                type="radio"
                                checked={manageToEveryone}
                                onChange={() => setManageToEveryone(true)}
                                className="text-cerulean focus:ring-cerulean"
                              />
                              Everyone in the family <span className="text-xs text-gray-400">(includes future members)</span>
                            </label>
                            <label className="flex items-center gap-2 text-sm text-gray-800">
                              <input
                                type="radio"
                                checked={!manageToEveryone}
                                onChange={() => setManageToEveryone(false)}
                                className="text-cerulean focus:ring-cerulean"
                              />
                              Specific family members
                            </label>
                            {!manageToEveryone && (
                              <div className="ml-6 space-y-1.5 max-h-64 overflow-y-auto rounded-xl border border-gray-200 p-3">
                                {(selectedFamily?.memberIDs || []).filter(id => id !== uid).map(memberId => (
                                  <label key={memberId} className="flex items-center gap-2 text-sm text-gray-800">
                                    <input
                                      type="checkbox"
                                      checked={manageRecipients.includes(memberId)}
                                      onChange={e => setManageRecipients(prev =>
                                        e.target.checked ? [...prev, memberId] : prev.filter(id => id !== memberId)
                                      )}
                                      className="rounded text-cerulean focus:ring-cerulean"
                                    />
                                    {memberName(memberId)}
                                  </label>
                                ))}
                                {(selectedFamily?.memberIDs || []).filter(id => id !== uid).length === 0 && (
                                  <p className="text-xs text-gray-400">No other family members yet.</p>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="flex gap-2 pt-2">
                            <button type="button" onClick={() => setView('detail')} className="btn-secondary flex-1">
                              Cancel
                            </button>
                            <button
                              type="button"
                              disabled={savingRecipients}
                              onClick={handleSaveRecipients}
                              className="btn-primary flex-1"
                            >
                              {savingRecipients ? 'Saving…' : 'Save'}
                            </button>
                          </div>
                        </div>
                      )}

                      {view === 'record' && detailLetter && detailContent && (
                        <LetterRecorder
                          familyId={selectedFamilyId}
                          letter={detailLetter}
                          body={detailContent.body}
                          uid={uid}
                          onRecordedChange={isRecorded => setDetailLetter(prev => (prev ? { ...prev, isRecorded } : prev))}
                        />
                      )}
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
